import { getDistinctObjectValues, getDistinctValues, isEmpty, isFalse, isTrue } from "../../../../utils/helpers";
import { getEffPartList, getEffectiveExtraPercent, getEffectiveFpoExtraPercent } from "../../../../helpers/orderHelper";
import { OrderFlowType, UOM } from "../../../../constants";
import { convert } from "../../../../helpers/unitConversionHelper";

const joinChar = "#@#";

const fpoIdentifier = record => `${record?.primaryUnitId || record?.unitId || ""}${joinChar}${record?.fpo || ""}`;

//* Extract only key from flowInfo which are needed to shown in columns
const sanitizeFlowInfo = (flowInfoList, order) => {
  if (isEmpty(flowInfoList)) return null;
  const flowInfo = flowInfoList[0];
  const productList = getDistinctObjectValues(flowInfoList.map(e => e.product));

  const finalFlowInfoList = flowInfoList.map(fi => ({
    ...fi,
    orderQty: isEmpty(fi.orderParentId) ? fi.orderQty : order?.flowInfoList?.find(e => e.id === fi.orderParentId)?.orderQty || fi.orderQty,
    extra: isEmpty(fi.orderParentId) ? fi.extra : order?.flowInfoList?.find(e => e.id === fi.orderParentId)?.extra || fi.extra
  }));

  const parentProductList = getDistinctObjectValues(
    flowInfoList
      .map(fi => order?.flowInfoList?.find(e => e.id === fi.parentId))
      .filter(e => !isEmpty(e))
      .map(e => e.product)
  );
  const uomQty = productList.length > 1 && parentProductList.length > 0 && parentProductList.some(e => isTrue(e.set)) ? "Set" : "Pc";

  let orderQty = finalFlowInfoList.reduce((acc, fi) => acc + fi.orderQty, 0);
  let fpoQty = finalFlowInfoList.reduce((acc, fi) => acc + (fi.fpoQty || 0), 0);
  if (uomQty === "Set") {
    const product = productList[0];
    const pfiList = flowInfoList.filter(e => e.product?.id === product?.id);
    const ratio = pfiList[0]?.ratio || 1;
    orderQty = pfiList.reduce((acc, fi) => acc + fi.orderQty, 0) / ratio;
    fpoQty = pfiList.reduce((acc, fi) => acc + fi.fpoQty, 0) / ratio;
  }

  return {
    product: productList.length === 1 ? flowInfo.product : null,
    productName: getDistinctValues(flowInfoList.map(fi => fi.product?.name)).join(", "),
    style: getDistinctValues(flowInfoList.map(fi => fi.style)).join(", "),
    color: getDistinctValues(flowInfoList.map(fi => fi.color)).join(", "),
    styleColor: flowInfo.styleColor,
    orderQty,
    fpoQty,
    extra: getEffectiveExtraPercent(finalFlowInfoList),
    fpoExtra: getEffectiveFpoExtraPercent(flowInfoList)
  };
};

export const getReportFlowInfoList = order => {
  if (isEmpty(order?.flowInfoList)) return [];
  const flowInfoList = order.flowInfoList.filter(fi => isEmpty(fi.orderParentId));
  const parentList = flowInfoList.filter(fi => isEmpty(fi.parentId));
  const fpoLevel = order?.fpoLevel || "BPO";

  if (fpoLevel === "COLOR") {
    return parentList.flatMap(parent => {
      const children = flowInfoList.filter(fi => fi.parentId === parent.id);
      const partList = isEmpty(children) ? parent.partList : getEffPartList(children, order);
      const unitChildren = order.flowInfoList.filter(fi => fi.orderParentId === parent.id);
      const fpoIds = getDistinctValues(unitChildren.map(u => fpoIdentifier(u)));

      //TODO: Set handling pending for COLOR level FPO

      // here there may children due to destination multiple unit or due split qty in multiple unit
      return partList
        .sort((a, b) => a.serial - b.serial)
        .flatMap(part => {
          let childList = [];
          let groupOrderList = [];
          let allocOrder;
          if (fpoIds?.length > 1) {
            childList = fpoIds.map(fpoId => {
              const fiList = unitChildren.filter(u => fpoIdentifier(u) === fpoId);
              const flowInfo = fiList[0];

              const allocOrder = order?.orderFlowList?.find(
                of =>
                  of.type === OrderFlowType.ALLOCATION &&
                  of.productIdList?.includes(flowInfo.product?.id) &&
                  of.styleColorList?.includes(flowInfo.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName
              );
              const groupOrderList = order?.orderFlowList
                ?.filter(of => of.type === OrderFlowType.GROUPING)
                ?.filter(
                  of =>
                    of.productIdList?.includes(flowInfo.product?.id) &&
                    of.styleColorList?.includes(flowInfo.styleColor) &&
                    isEmpty(of.parentColor) &&
                    of.part === part.effPartName &&
                    fpoIdentifier(of) === fpoId &&
                    !isEmpty(of.unitId)
                );
              return {
                ...sanitizeFlowInfo(fiList, order),
                key: `${fiList.map(e => e.id).join("_")}-${part.effPartName}`,
                parentKey: `${parent.id}-${part.effPartName}`,
                isFpo: true,
                order,
                allocOrder,
                groupOrderList,
                part,
                effPartName: part.effPartName,
                bomCu: part.bomCu,
                ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
              };
            });

            allocOrder = order?.orderFlowList?.find(
              of =>
                of.type === OrderFlowType.ALLOCATION &&
                of.productIdList?.includes(parent.product?.id) &&
                of.styleColorList?.includes(parent.styleColor) &&
                isEmpty(of.parentColor) &&
                of.part === part.effPartName
            );
            groupOrderList = order?.orderFlowList
              ?.filter(of => of.type === OrderFlowType.GROUPING)
              ?.filter(
                of =>
                  of.productIdList?.includes(parent.product?.id) &&
                  of.styleColorList?.includes(parent.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName &&
                  fpoIds.includes(fpoIdentifier(of)) &&
                  !isEmpty(of.unitId)
              );
          } else {
            allocOrder = order?.orderFlowList?.find(
              of =>
                of.type === OrderFlowType.ALLOCATION &&
                of.productIdList?.includes(parent.product?.id) &&
                of.styleColorList?.includes(parent.styleColor) &&
                isEmpty(of.parentColor) &&
                of.part === part.effPartName
            );
            groupOrderList = order?.orderFlowList
              ?.filter(of => of.type === OrderFlowType.GROUPING)
              ?.filter(
                of =>
                  of.productIdList?.includes(parent.product?.id) &&
                  of.styleColorList?.includes(parent.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName &&
                  fpoIdentifier(of) === fpoIds[0] &&
                  !isEmpty(of.unitId)
              );
          }

          const summary = !isEmpty(childList)
            ? sanitizeFlowInfo(childList, order)
            : !isEmpty(unitChildren)
            ? sanitizeFlowInfo(unitChildren, order)
            : sanitizeFlowInfo([parent], order);

          return [
            {
              ...summary,
              key: `${parent.id}-${part.effPartName}`,
              order,
              allocOrder,
              groupOrderList,
              part,
              effPartName: part.effPartName,
              bomCu: part.bomCu,
              ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
              uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
              isParent: !isEmpty(childList)
            },
            ...childList
          ];
        });
    });
  } else {
    return parentList.flatMap(parent => {
      const children = flowInfoList.filter(fi => fi.parentId === parent.id);
      // 1. Simple Order without any child (without destination)
      if (isEmpty(children)) {
        // here there may children due to qty split in multiple unit
        // Flat multiple record due to Part
        return parent.partList
          .sort((a, b) => a.serial - b.serial)
          .flatMap(part => {
            const cutOrderList = order?.orderFlowList
              ?.filter(of => of.type === OrderFlowType.CUTPLAN)
              ?.filter(
                of =>
                  of.productIdList?.includes(parent.product?.id) &&
                  of.styleColorList?.includes(parent.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName
              );

            const summary = sanitizeFlowInfo([parent], order);
            return [
              {
                ...summary,
                key: `${parent.id}-${part.effPartName}`,
                order,
                cutOrderList,
                part,
                effPartName: part.effPartName,
                bomCu: part.bomCu,
                ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                flowInfoList: [parent],
                isParent: false
              }
            ];
          });
      }

      // 2. Set Order whithout destination
      if (isTrue(parent.isSetParent)) {
        // Ideally for Set, Each child will be different based on product, style and color
        const styleColorList = getDistinctValues(children.map(c => c.styleColor));
        const childList = styleColorList.flatMap(styleColor => {
          const childFlowInfoList = children.filter(c => styleColor === c.styleColor);
          const partList = getEffPartList(childFlowInfoList, order);
          const child = childFlowInfoList[0];

          // here there may children due to qty split in multiple unit
          return partList
            .sort((a, b) => a.serial - b.serial)
            .flatMap(part => {
              const cutOrderList = order?.orderFlowList
                ?.filter(of => of.type === OrderFlowType.CUTPLAN)
                ?.filter(
                  of =>
                    of.productIdList?.includes(child.product?.id) &&
                    of.styleColorList?.includes(child.styleColor) &&
                    of.parentColor === parent.color &&
                    of.part === part.effPartName
                );

              return cutOrderList.map(co => {
                const coFlowInfoList = childFlowInfoList.filter(cfi => co.productIdList.includes(cfi.product?.id));
                const summary = sanitizeFlowInfo(coFlowInfoList, order);
                return {
                  ...summary,
                  key: `${child.id}-${part?.effPartName}`,
                  parentKey: `${parent.id}`,
                  parentColor: parent.color,
                  parentProductId: parent.product?.id,
                  order,
                  cutOrderList,
                  flowInfoList: coFlowInfoList,
                  part,
                  effPartName: part.effPartName,
                  bomCu: part.bomCu,
                  ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                  uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                  uomQty: co.productIdList?.length > 1 ? "Set" : "Pc"
                };
              });
            });
        });
        return [{ ...sanitizeFlowInfo([parent], order), key: `${parent.id}`, order, isParent: true, uomQty: "Set" }, ...childList];
      } else {
        const isNonSet = children.every(c => isFalse(c.isSetParent));

        // 3. Simple Order with destination
        if (isNonSet) {
          const partList = getEffPartList(children, order);

          // here there may children due to destination multiple unit or due split qty in multiple unit
          return partList
            .sort((a, b) => a.serial - b.serial)
            .flatMap(part => {
              const cutOrderList = order?.orderFlowList
                ?.filter(of => of.type === OrderFlowType.CUTPLAN)
                ?.filter(
                  of =>
                    of.productIdList?.includes(parent.product?.id) &&
                    of.styleColorList?.includes(parent.styleColor) &&
                    isEmpty(of.parentColor) &&
                    of.part === part.effPartName
                );

              const summary = sanitizeFlowInfo(children, order);

              return [
                {
                  ...summary,
                  key: `${parent.id}-${part.effPartName}`,
                  order,
                  cutOrderList,
                  flowInfoList: children,
                  part,
                  effPartName: part.effPartName,
                  bomCu: part.bomCu,
                  ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                  uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                  isParent: false
                }
              ];
            });
        }
        // 4. Set Order with destination
        else {
          const deepChildren = children.flatMap(child => {
            const childList = flowInfoList.filter(fi => fi.parentId === child.id);
            return isEmpty(childList) ? [child] : childList;
          });
          const styleColorList = getDistinctValues(deepChildren.map(c => c.styleColor));

          const childList = styleColorList.flatMap(styleColor => {
            const childFlowInfoList = deepChildren.filter(c => styleColor === c.styleColor);
            const partList = getEffPartList(childFlowInfoList, order);

            // here there may children due to destination multiple unit or due split qty in multiple unit
            return partList
              .sort((a, b) => a.serial - b.serial)
              .flatMap(part => {
                const productIds = getDistinctValues(childFlowInfoList.map(c => c.product?.id));
                const cutOrderList = order?.orderFlowList
                  ?.filter(of => of.type === OrderFlowType.CUTPLAN)
                  ?.filter(
                    of =>
                      of.productIdList?.every(id => productIds.includes(id)) &&
                      of.styleColorList?.includes(styleColor) &&
                      of.parentColor === parent.color &&
                      of.part === part.effPartName
                  );

                return cutOrderList.map(co => {
                  const coFlowInfoList = childFlowInfoList.filter(cfi => co.productIdList.includes(cfi.product?.id));
                  const summary = sanitizeFlowInfo(coFlowInfoList, order);
                  return {
                    ...summary,
                    key: `${coFlowInfoList.map(e => e.id).join("_")}-${part?.effPartName}`,
                    parentKey: `${parent.id}`,
                    parentColor: parent.color,
                    parentProductId: parent.product?.id,
                    order,
                    flowInfoList: coFlowInfoList,
                    cutOrderList,
                    part,
                    effPartName: part.effPartName,
                    bomCu: part.bomCu,
                    ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                    uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                    uomQty: co.productIdList?.length > 1 ? "Set" : "Pc"
                  };
                });
              });
          });

          return [{ ...parent, ...sanitizeFlowInfo([parent], order), key: `${parent.id}`, order, isParent: true, uomQty: "Set" }, ...childList];
        }
      }
    });
  }
};
